<template>
  <div
    class="app-shell"
    :data-shell="shellMode"
    :data-side-theme="sideTheme"
    :data-collapsed="String(sidebarCollapsed)"
    :data-drawer="drawerOpen ? 'open' : 'closed'"
  >

    <!-- ─────────────────────────────────────────────
         OMS shell (logged-in admin / affiliate roles)
         ───────────────────────────────────────────── -->
    <template v-if="shellMode === 'oms'">
      <!-- Sidebar -->
      <aside class="oms-side">
        <router-link :to="homeRoute" class="oms-side__brand">
          <img :src="shellLogo" :alt="shellBrandName" class="oms-side__brand-img" />
          <div class="oms-side__brand-meta">
            <div class="oms-side__brand-name">{{ shellBrandName }}</div>
            <div class="oms-side__brand-sub">{{ shellBrandSub }}</div>
          </div>
        </router-link>

        <div class="oms-side__section-label">兼職訂單系統</div>
        <nav class="oms-side__nav">
          <router-link
            v-for="item in navItems.filter(item => item.to !== '/admin/tenants')"
            :key="item.to"
            :to="item.to"
            class="oms-side__link"
            :title="sidebarCollapsed ? item.label : undefined"
            @click="onNavClick"
          >
            <i :class="`fas ${item.icon} oms-side__icon`"></i>
            <span class="oms-side__label">{{ item.label }}</span>
          </router-link>
        </nav>

        <template v-if="navItems.some(item => item.to === '/admin/tenants')">
          <div class="oms-side__divider" role="separator"></div>
          <div class="oms-side__section-label">靜態頁</div>
          <nav class="oms-side__nav oms-side__nav--static" aria-label="靜態頁">
            <router-link to="/admin/tenants" class="oms-side__link" :title="sidebarCollapsed ? '靜態頁管理' : undefined" @click="onNavClick"><i class="fas fa-window-maximize oms-side__icon"></i><span class="oms-side__label">靜態頁管理</span></router-link>
          </nav>
        </template>

        <div class="oms-side__footer">
          <router-link
            v-if="userStore.canAccessAdmin('/admin/settings')"
            to="/admin/settings"
            class="oms-side__link"
            :title="sidebarCollapsed ? '系統設定' : undefined"
            @click="onNavClick"
          >
            <i class="fas fa-cog oms-side__icon"></i>
            <span class="oms-side__label">系統設定</span>
          </router-link>

          <router-link to="/change-password" class="oms-side__link"
                       :title="sidebarCollapsed ? '修改密碼' : undefined" @click="onNavClick">
            <i class="fas fa-key oms-side__icon"></i>
            <span class="oms-side__label">修改密碼</span>
          </router-link>

          <button class="oms-side__link oms-side__link--danger" @click="handleLogout"
                  :title="sidebarCollapsed ? '登出' : undefined">
            <i class="fas fa-sign-out-alt oms-side__icon"></i>
            <span class="oms-side__label">登出</span>
          </button>

          <div class="oms-side__user">
            <i class="fas fa-user-circle oms-side__user-icon"></i>
            <div class="oms-side__user-info">
              <div class="oms-side__user-name">
                {{ userStore.userProfile?.displayName || userStore.currentUser?.email }}
              </div>
              <div class="oms-side__user-role">{{ userRoleText }}</div>
            </div>
          </div>
        </div>
      </aside>

      <!-- Mobile drawer scrim -->
      <div class="oms-scrim" @click="drawerOpen = false"></div>

      <!-- Top bar -->
      <header class="oms-top">
        <button
          class="oms-top__toggle"
          :aria-label="shellToggleLabel"
          :aria-expanded="isMobile ? drawerOpen : !sidebarCollapsed"
          @click="toggleShell"
        >
          <i class="fas fa-bars"></i>
        </button>

        <nav class="oms-top__crumbs" aria-label="breadcrumb">
          <span class="oms-top__crumb">{{ userRoleText || '美好假期' }}</span>
          <span class="oms-top__crumb-sep">/</span>
          <span class="oms-top__crumb oms-top__crumb--current">{{ currentScreenLabel }}</span>
        </nav>

        <div class="oms-top__actions">
          <NotificationBell v-if="!userStore.isTenantMember || userStore.isAdmin" />
        </div>
      </header>

      <!-- Main -->
      <main :class="['oms-main', { 'oms-main--studio': isWebsiteEditorPage }]">
        <!-- Status banners -->
        <div
          v-if="userStore.isAffiliate && (userStore.affiliateProfile?.status as string) === 'pending'"
          class="status-banner status-banner--warning"
        >
          <div class="status-banner__inner">
            <div class="status-icon"><div class="pulse-dot"></div></div>
            <div class="status-banner__body">
              <div class="status-title">審核中</div>
              <div class="status-message">您的兼職申請正在審核中，請耐心等待管理員審核通過</div>
            </div>
          </div>
        </div>

        <div
          v-if="userStore.isAffiliate && userStore.affiliateProfile?.status === 'suspended'"
          class="status-banner status-banner--danger"
        >
          <div class="status-banner__inner">
            <div class="status-icon"><i class="fas fa-exclamation-triangle"></i></div>
            <div class="status-banner__body">
              <div class="status-title">帳號暫停</div>
              <div class="status-message">您的帳號已被暫停，如有疑問請聯繫客服</div>
            </div>
          </div>
        </div>

        <div class="oms-main__inner"><slot /></div>
      </main>
    </template>


    <!-- ─────────────────────────────────────────────
         Public / auth shell (login, apply, order lookup…)
         ───────────────────────────────────────────── -->
    <template v-else>
      <header v-if="!isAuthPage && !isPartnerPage && !isTenantSitePage" class="pub-top">
        <div class="pub-top__inner">
          <router-link :to="homeRoute" class="pub-top__brand">
            <img src="/logo.png" alt="美好假期" class="pub-top__brand-img" />
          </router-link>

          <div class="pub-top__actions">
            <router-link
              v-if="isOrderLookupPage"
              to="/apply"
              class="pub-top__btn pub-top__btn--ghost"
            >
              <i class="fas fa-arrow-left"></i><span>返回報名</span>
            </router-link>
            <router-link
              v-else-if="isApplyPage"
              to="/order-status"
              class="pub-top__btn"
            >
              <i class="fas fa-search"></i><span>訂單查詢</span>
            </router-link>

            <router-link
              v-if="!userStore.isLoggedIn && !isPublicPage"
              to="/login"
              class="pub-top__btn pub-top__btn--outline"
            >
              登入
            </router-link>
          </div>
        </div>
      </header>

      <main class="pub-main">
        <slot />
      </main>

      <footer v-if="!isAuthPage && !isPartnerPage && !isTenantSitePage" class="pub-footer">
        <div class="pub-footer__inner">
          © {{ currentYear }} 美好假期. All rights reserved.
        </div>
      </footer>
    </template>

  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useUserStore } from '@/store/user'
import NotificationBell from '@/components/NotificationBell.vue'

const userStore = useUserStore()
const router = useRouter()
const route = useRoute()
const currentYear = new Date().getFullYear()

// ── Shell state ─────────────────────────────────
const sidebarCollapsed = ref(localStorage.getItem('sidebar-collapsed') === 'true')
const drawerOpen = ref(false)
const sideTheme = ref<'dark' | 'light'>(
  (localStorage.getItem('side-theme') as 'dark' | 'light') || 'light'
)

watch(sidebarCollapsed, (val) => localStorage.setItem('sidebar-collapsed', String(val)))
watch(sideTheme, (val) => localStorage.setItem('side-theme', val))

const isMobile = ref(typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches)
const onResize = () => {
  isMobile.value = window.matchMedia('(max-width: 1023px)').matches
  if (!isMobile.value) drawerOpen.value = false
}

const toggleShell = () => {
  if (isMobile.value) drawerOpen.value = !drawerOpen.value
  else sidebarCollapsed.value = !sidebarCollapsed.value
}

const onNavClick = () => {
  if (isMobile.value) drawerOpen.value = false
}

// ── Route classification ────────────────────────
const isApplyPage = computed(() => ['/apply', '/r/'].some(p => route.path.startsWith(p)))
const isOrderLookupPage = computed(() => route.path.startsWith('/order-status'))
const isPartnerPage = computed(() => route.path.startsWith('/partner/'))
const isTenantSitePage = computed(() => route.path.startsWith('/sites/') || route.path === '/website-preview')
const isWebsiteEditorPage = computed(() => route.path === '/agency/website' || /^\/admin\/tenants\/[^/]+\/website$/.test(route.path))

const isPublicPage = computed(() => {
  const publicPages = ['/apply', '/join', '/r/', '/terms', '/privacy', '/order-status', '/partner/', '/sites/', '/website-preview']
  return publicPages.some(p => route.path.startsWith(p))
})

const isAuthPage = computed(() => {
  const authPages = ['/login', '/register', '/forgot-password', '/reset-password']
  return authPages.some(p => route.path.startsWith(p))
})

// 'oms' shell only when user is logged-in AND on a private (non-public, non-auth) route
const shellMode = computed<'oms' | 'public'>(() => {
  if (!userStore.isLoggedIn) return 'public'
  if (isAuthPage.value) return 'public'
  if (isPublicPage.value) return 'public'
  return 'oms'
})

// ── Navigation items by role ────────────────────
interface NavItem { to: string; icon: string; label: string }

const navItems = computed<NavItem[]>(() => {
  if (userStore.isAffiliate) {
    return [
      { to: '/affiliate/dashboard', icon: 'fa-chart-line',     label: '儀表板' },
      { to: '/affiliate/products',  icon: 'fa-map-location-dot', label: '商品資訊' },
      { to: '/affiliate/bookings',  icon: 'fa-clipboard-list', label: '訂單列表' },
      { to: '/affiliate/links',     icon: 'fa-share-alt',      label: '素材與連結' },
      { to: '/affiliate/profile',   icon: 'fa-user',           label: '個人設定' },
    ]
  }
  if (userStore.isAdmin) return userStore.adminMenu
  if (userStore.isTenantMember) {
    return [
      { to: '/agency/dashboard', icon: 'fa-chart-line', label: '營運總覽' },
      { to: '/agency/products', icon: 'fa-route', label: '品牌商品' },
      { to: '/agency/inquiries', icon: 'fa-comments', label: '詢價管理' },
      { to: '/agency/orders', icon: 'fa-file-invoice-dollar', label: '人工收款' },
      ...(userStore.canManageTenant ? [{ to: '/agency/website', icon: 'fa-window-maximize', label: '品牌網站' }] : []),
      { to: '/agency/settings', icon: 'fa-palette', label: '品牌設定' },
    ]
  }
  return []
})

// ── Breadcrumb / labels ─────────────────────────
const ROUTE_LABELS: Record<string, string> = {
  '/admin/orders':       '訂單管理',
  '/admin/contacts':     '客戶管理',
  '/admin/affiliates':   '兼職管理',
  '/admin/applications': '申請審核',
  '/admin/products':     '產品管理',
  '/admin/payouts':      '撥款管理',
  '/admin/funnel':       '漏斗分析',
  '/admin/users':        '用戶管理',
  '/admin/settings':     '系統設定',
  '/admin/tenants':      '靜態頁管理',
  '/admin/settlement':   '結算管理',
  '/affiliate/dashboard': '儀表板',
  '/affiliate/products':  '商品資訊中心',
  '/affiliate/bookings':  '訂單列表',
  '/affiliate/links':     '素材與連結',
  '/affiliate/profile':   '個人設定',
  '/change-password':     '修改密碼',
  '/agency/dashboard':    '營運總覽',
  '/agency/products':     '品牌商品',
  '/agency/inquiries':    '詢價管理',
  '/agency/orders':       '人工收款',
  '/agency/website':      '品牌網站',
  '/agency/settings':     '品牌設定',
}

const shellToggleLabel = computed(() => {
  if (isMobile.value) return drawerOpen.value ? '關閉選單' : '開啟選單'
  return sidebarCollapsed.value ? '展開側欄' : '收合側欄'
})

const currentScreenLabel = computed(() => {
  // exact match first, then prefix match
  if (ROUTE_LABELS[route.path]) return ROUTE_LABELS[route.path]
  for (const [k, v] of Object.entries(ROUTE_LABELS)) {
    if (route.path.startsWith(k)) return v
  }
  return ''
})

const homeRoute = computed(() => {
  if (userStore.isAdmin) return userStore.adminHome
  if (userStore.isSales) return '/admin/contacts'
  if (userStore.isAccounting) return '/admin/payouts'
  if (userStore.isOP) return '/admin/orders'
  if (userStore.isAdmin) return '/admin/affiliates'
  if (userStore.isAffiliate) return '/affiliate/dashboard'
  if (userStore.isTenantMember) return '/agency/dashboard'
  return '/'
})

const userRoleText = computed(() => {
  if (userStore.isSales) return '業務專員'
  if (userStore.isAccounting) return '會計'
  if (userStore.isOP) return 'OP 專員'
  if (userStore.isAdmin) return '系統管理員'
  if (userStore.isAffiliate) return '兼職人員'
  if (userStore.isTenantMember) {
    const labels = { owner: '旅行社負責人', admin: '旅行社管理員', agent: '承辦人員', viewer: '唯讀人員' }
    return labels[userStore.userProfile?.tenantRole || 'viewer']
  }
  return ''
})

const shellBrandName = computed(() => userStore.isTenantMember && !userStore.isAdmin
  ? userStore.tenantProfile?.brandName || '同業工作台'
  : '美好假期')
const shellBrandSub = computed(() => userStore.isTenantMember && !userStore.isAdmin
  ? 'AGENCY · 同業中台'
  : 'OMS · 兼職系統')
const shellLogo = computed(() => userStore.isTenantMember && !userStore.isAdmin
  ? userStore.tenantProfile?.branding?.logoUrl || '/logo.png'
  : '/logo.png')

// ── Logout ──────────────────────────────────────
const handleLogout = async () => {
  try {
    drawerOpen.value = false
    await userStore.logout()
    router.push('/login')
  } catch (error) {
    console.error('Logout error:', error)
  }
}

// Close drawer on route change
watch(() => route.path, () => { drawerOpen.value = false })

onMounted(() => {
  window.addEventListener('resize', onResize)
})
onUnmounted(() => {
  window.removeEventListener('resize', onResize)
})
</script>

<style scoped>
/* ═══════════════════════════════════════════════
   App shell root
   ═══════════════════════════════════════════════ */
.app-shell {
  min-height: 100vh;
  background: var(--c-canvas);
  color: var(--c-ink-900);
  font-family: var(--font-sans);
}

/* ═══════════════════════════════════════════════
   OMS shell — sidebar + topbar + main
   ═══════════════════════════════════════════════ */
.app-shell[data-shell="oms"] {
  display: grid;
  grid-template-columns: var(--side-w) 1fr;
  grid-template-rows: var(--nav-h) minmax(0, 1fr);
  grid-template-areas:
    "side head"
    "side main";
  height: 100dvh;
  min-height: 0;
  overflow: hidden;
  transition: grid-template-columns var(--t-base);
}

.app-shell[data-shell="oms"][data-collapsed="true"] {
  grid-template-columns: var(--side-w-collapsed) 1fr;
}

/* ── Sidebar ─────────────────────────────────── */
.oms-side {
  grid-area: side;
  background: var(--c-navy-700);
  color: #DCE6EE;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-right: 1px solid var(--c-navy-800);
  position: relative;
  z-index: 5;
  transition: width var(--t-base);
}

.app-shell[data-side-theme="light"] .oms-side {
  background: var(--c-paper);
  color: var(--c-ink-700);
  border-right: var(--bd-1);
}

.oms-side__brand {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  height: var(--nav-h);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
  text-decoration: none;
  color: inherit;
}
.app-shell[data-side-theme="light"] .oms-side__brand {
  border-bottom: var(--bd-1-soft);
}
.oms-side__brand-img {
  height: 40px;
  width: auto;
  max-width: 100%;
  object-fit: contain;
  flex-shrink: 0;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
}
.app-shell[data-side-theme="light"] .oms-side__brand-img { filter: none; }

.oms-side__brand-meta {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  overflow: hidden;
}
.oms-side__brand-name {
  font-size: 15px;
  font-weight: var(--fw-bold);
  letter-spacing: 0.04em;
  white-space: nowrap;
}
.oms-side__brand-sub {
  font-size: 10px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  opacity: 0.55;
  white-space: nowrap;
}

.app-shell[data-collapsed="true"] .oms-side__brand-meta { display: none; }
.app-shell[data-collapsed="true"] .oms-side__brand {
  justify-content: center;
  padding: 14px 8px;
}
.app-shell[data-collapsed="true"] .oms-side__brand-img {
  height: auto;
  max-height: 36px;
  max-width: 100%;
}

.oms-side__section-label {
  padding: 18px 20px 6px;
  font-size: 10px;
  font-weight: var(--fw-semibold);
  letter-spacing: 0.14em;
  text-transform: uppercase;
  opacity: 0.5;
}
.app-shell[data-collapsed="true"] .oms-side__section-label {
  text-align: center;
  padding: 18px 4px 6px;
  font-size: 8px;
  letter-spacing: 0.08em;
}

.oms-side__nav {
  flex: 1;
  overflow-y: auto;
  padding: 6px 12px 12px;
}

.oms-side__link {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 12px;
  border-radius: var(--r-md);
  color: inherit;
  text-decoration: none;
  font-size: var(--fs-base);
  white-space: nowrap;
  margin-bottom: 2px;
  background: none;
  border: 0;
  width: 100%;
  text-align: left;
  cursor: pointer;
  transition: background-color var(--t-fast), color var(--t-fast);
}
.oms-side__link:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
}
.app-shell[data-side-theme="light"] .oms-side__link:hover {
  background: var(--c-teal-50);
  color: var(--c-teal-600);
}

/* Vue Router active class */
.oms-side__link.router-link-active,
.oms-side__link.router-link-exact-active {
  background: var(--c-teal-400);
  color: #fff;
  font-weight: var(--fw-semibold);
}
.app-shell[data-side-theme="light"] .oms-side__link.router-link-active,
.app-shell[data-side-theme="light"] .oms-side__link.router-link-exact-active {
  background: var(--c-teal-50);
  color: var(--c-teal-600);
}

.oms-side__icon {
  width: 18px;
  text-align: center;
  flex-shrink: 0;
  font-size: 14px;
  pointer-events: none;
}
.oms-side__label {
  overflow: hidden;
  transition: opacity 0.18s ease, max-width 0.22s ease;
  max-width: 200px;
}
.app-shell[data-collapsed="true"] .oms-side__label {
  opacity: 0;
  max-width: 0;
}
.app-shell[data-collapsed="true"] .oms-side__link {
  gap: 0;
  justify-content: center;
}

/* Sidebar footer */
.oms-side__footer {
  padding: 10px 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
}
.app-shell[data-side-theme="light"] .oms-side__footer {
  border-top: var(--bd-1-soft);
}

.oms-side__link--danger {
  color: #FFB5A6 !important;
}
.oms-side__link--danger:hover {
  background: rgba(220, 38, 38, 0.18) !important;
  color: #fff !important;
}
.app-shell[data-side-theme="light"] .oms-side__link--danger {
  color: var(--c-danger) !important;
}
.app-shell[data-side-theme="light"] .oms-side__link--danger:hover {
  background: var(--c-danger-bg) !important;
  color: var(--c-danger) !important;
}

.oms-side__user {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
  padding: 10px 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  overflow: hidden;
}
.app-shell[data-side-theme="light"] .oms-side__user {
  border-top: var(--bd-1-soft);
}
.oms-side__user-icon {
  font-size: 22px;
  color: var(--c-teal-300);
  flex-shrink: 0;
}
.app-shell[data-side-theme="light"] .oms-side__user-icon {
  color: var(--c-teal-500);
}
.oms-side__user-info {
  min-width: 0;
  overflow: hidden;
  transition: opacity 0.18s ease, max-width 0.22s ease;
  max-width: 160px;
}
.app-shell[data-collapsed="true"] .oms-side__user-info {
  opacity: 0;
  max-width: 0;
}
.app-shell[data-collapsed="true"] .oms-side__user {
  gap: 0;
  justify-content: center;
}
.oms-side__user-name {
  font-size: var(--fs-sm);
  font-weight: var(--fw-medium);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.oms-side__user-role {
  font-size: var(--fs-xs);
  opacity: 0.6;
}

/* ── Topbar ─────────────────────────────────── */
.oms-top {
  grid-area: head;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 18px;
  background: var(--c-paper);
  border-bottom: var(--bd-1);
  height: var(--nav-h);
  position: sticky;
  top: 0;
  z-index: 4;
}

.oms-top__toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: var(--r-sm);
  border: var(--bd-1);
  background: var(--c-paper);
  color: var(--c-ink-600);
  cursor: pointer;
  transition: background-color var(--t-fast), color var(--t-fast);
  flex-shrink: 0;
}
.oms-top__toggle:hover {
  background: var(--c-ink-50);
  color: var(--c-teal-600);
  border-color: var(--c-teal-300);
}

.oms-top__crumbs {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
}
.oms-top__crumb {
  font-size: var(--fs-base);
  color: var(--c-ink-500);
  text-overflow: ellipsis;
  overflow: hidden;
}
.oms-top__crumb--current {
  color: var(--c-ink-900);
  font-weight: var(--fw-semibold);
}
.oms-top__crumb-sep {
  color: var(--c-ink-300);
  font-size: var(--fs-base);
  flex-shrink: 0;
}

.oms-top__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

/* ── Main ───────────────────────────────────── */
.oms-main {
  grid-area: main;
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  background: var(--c-canvas);
}
.oms-main--studio { overflow:hidden; }
.oms-main--studio .oms-main__inner { width:100%; height:100%; min-height:0; overflow:hidden; }

.oms-main__inner {
  /* Page content from <slot /> renders here. Pages own their own padding;
     we don't reapply container padding so existing pages render unchanged. */
}

/* ── Status banners (logged-in OMS shell) ─── */
.status-banner {
  border-bottom: 2px solid;
  position: relative;
  overflow: hidden;
}
.status-banner::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.1) 50%, transparent 100%);
  animation: shimmer 2s infinite;
}
.status-banner__inner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 24px;
  position: relative;
  z-index: 1;
}
.status-banner--warning {
  background: linear-gradient(135deg, var(--c-warning-bg) 0%, #FEF3C7 100%);
  border-color: var(--c-warning);
  color: var(--c-warning-fg);
}
.status-banner--danger {
  background: linear-gradient(135deg, var(--c-danger-bg) 0%, #FEE2E2 100%);
  border-color: var(--c-danger);
  color: var(--c-danger-fg);
}
.status-icon {
  width: 36px;
  height: 36px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.status-banner__body { flex: 1; min-width: 0; }
.status-title { font-weight: var(--fw-semibold); font-size: var(--fs-base); }
.status-message { font-size: var(--fs-sm); opacity: 0.9; }
.pulse-dot {
  width: 12px; height: 12px;
  border-radius: 50%;
  background: currentColor;
  animation: pulse 2s infinite;
}

/* ── Mobile drawer ──────────────────────────── */
.oms-scrim { display: none; }

@media (max-width: 1023px) {
  .app-shell[data-shell="oms"] {
    grid-template-columns: 0 1fr;
  }
  .app-shell[data-shell="oms"][data-collapsed="true"] {
    grid-template-columns: 0 1fr;
  }
  .oms-side {
    position: fixed;
    top: 0;
    left: 0;
    width: 280px;
    height: 100vh;
    transform: translateX(-100%);
    transition: transform var(--t-base);
    z-index: 60;
    box-shadow: var(--sh-lg);
  }
  .app-shell[data-drawer="open"] .oms-side {
    transform: translateX(0);
  }
  .app-shell[data-drawer="open"] .oms-scrim {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(8, 30, 47, 0.45);
    z-index: 50;
    animation: scrimFade 0.2s ease;
  }
  /* On mobile, sidebar is never collapsed (it's a drawer) — undo collapsed-state hides */
  .app-shell[data-collapsed="true"] .oms-side__brand-meta { display: flex; }
  .app-shell[data-collapsed="true"] .oms-side__brand {
    justify-content: flex-start;
    padding: 14px 18px;
  }
  .app-shell[data-collapsed="true"] .oms-side__label {
    opacity: 1;
    max-width: 200px;
  }
  .app-shell[data-collapsed="true"] .oms-side__link {
    gap: 12px;
    justify-content: flex-start;
  }
  .app-shell[data-collapsed="true"] .oms-side__user-info {
    opacity: 1;
    max-width: 160px;
  }
  .app-shell[data-collapsed="true"] .oms-side__user {
    gap: 10px;
    justify-content: flex-start;
  }
}

/* ═══════════════════════════════════════════════
   Public / auth shell
   ═══════════════════════════════════════════════ */
.app-shell[data-shell="public"] {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: var(--c-bg);
}

.pub-top {
  background: linear-gradient(135deg, var(--c-bg) 0%, var(--c-ink-50) 100%);
  border-bottom: 2px solid var(--c-border);
  position: sticky;
  top: 0;
  z-index: var(--z-sticky);
  backdrop-filter: blur(10px);
  box-shadow: var(--shadow-sm);
}
.pub-top__inner {
  max-width: var(--container-xl);
  margin: 0 auto;
  height: var(--nav-h);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 0 var(--layout-md);
}
.pub-top__brand {
  text-decoration: none;
  color: inherit;
  display: flex;
  align-items: center;
  transition: transform var(--t-fast);
}
.pub-top__brand:hover { transform: scale(1.02); }
.pub-top__brand-img {
  height: 44px;
  width: auto;
  object-fit: contain;
  border-radius: var(--r-md);
}
.pub-top__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.pub-top__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: 1px solid var(--c-teal-200);
  border-radius: var(--r-pill);
  font-size: var(--fs-sm);
  font-weight: var(--fw-medium);
  color: var(--c-teal-700);
  text-decoration: none;
  background: var(--c-teal-50);
  transition: all var(--t-fast);
}
.pub-top__btn:hover {
  background: var(--c-teal-400);
  color: #fff;
  border-color: var(--c-teal-400);
}
.pub-top__btn--ghost {
  color: var(--c-ink-600);
  background: var(--c-ink-50);
  border-color: var(--c-ink-200);
}
.pub-top__btn--ghost:hover {
  background: var(--c-ink-600);
  color: #fff;
  border-color: var(--c-ink-600);
}
.pub-top__btn--outline {
  color: var(--c-ink-700);
  background: transparent;
  border-color: var(--c-ink-200);
}
.pub-top__btn--outline:hover {
  background: var(--c-ink-50);
  color: var(--c-ink-900);
  border-color: var(--c-ink-300);
}

.pub-main {
  flex: 1;
  background: linear-gradient(180deg, var(--c-bg) 0%, var(--c-ink-50) 100%);
}

.pub-footer {
  background: var(--c-paper);
  border-top: var(--bd-1);
}
.pub-footer__inner {
  max-width: var(--container-xl);
  margin: 0 auto;
  padding: var(--sp-6) var(--layout-md);
  text-align: center;
  font-size: var(--fs-sm);
  color: var(--c-ink-500);
}

/* ═══════════════════════════════════════════════
   Animations
   ═══════════════════════════════════════════════ */
@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%      { opacity: 0.7; transform: scale(1.1); }
}
@keyframes shimmer {
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}
@keyframes scrimFade {
  from { opacity: 0; }
  to   { opacity: 1; }
}

/* Make icons inside links non-interactive so clicks fall through to the link */
button i, .oms-side__link i, .pub-top__btn i { pointer-events: none; }
</style>

<style scoped>
.oms-side__divider{height:1px;flex:0 0 1px;background:var(--c-ink-200);opacity:.45;margin:12px 16px 4px}
.oms-side__nav--static{flex:0 0 auto}
</style>
